import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { OptionsList, OptionsListMulti } from 'gung-common';
import { Observable, Subject, debounceTime, distinctUntilChanged, merge, switchMap } from 'rxjs';
import {
  SalesInsightsActivitiesResponse,
  SalesInsightsActivityGroup,
  SalesInsightsActivityRow,
  SalesInsightsFilterValues
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService, SalesInsightsBulkResult } from '../../services/sales-insights-activity.service';
import {
  SalesInsightsDueKind,
  salesInsightsActivitySections,
  salesInsightsDayKey,
  salesInsightsDue,
  salesInsightsInitials,
  salesInsightsParseDate,
  salesInsightsTypeColorIndex
} from '../../utils/sales-insights-activities';
import { formatSalesInsightsAmount } from '../../utils/sales-insights-format';
import { salesInsightsLocale } from '../../utils/sales-insights-labels';
import { salesInsightsColor } from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export interface SalesInsightsActivityDueView {
  kind: SalesInsightsDueKind;
  label: string;
  sub: string;
}

export interface SalesInsightsActivityView {
  row: SalesInsightsActivityRow;
  day: string;
  time: string;
  due: SalesInsightsActivityDueView;
  typeName: string;
  typeRgb: string;
  typeIcon: string;
  statusName: string;
  responsibleName: string;
  responsibleInitials: string;
  responsibleRgb: string;
}

export interface SalesInsightsActivityViewSection {
  group: SalesInsightsActivityGroup;
  labelKey: string;
  icon: string;
  count: string;
  shown: string | null;
  rows: SalesInsightsActivityView[];
}

export interface SalesInsightsActivityColumns {
  customer: boolean;
  responsible: boolean;
}

type ActivityFilter = 'type' | 'status';

const SEARCH_DEBOUNCE_MS = 300;
const ALL = '';
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5];

const GROUP_ICONS: Record<SalesInsightsActivityGroup, string> = {
  overdue: 'fa-solid fa-circle-exclamation',
  today: 'fa-solid fa-sun',
  planned: 'fa-regular fa-calendar',
  done: 'fa-solid fa-circle-check'
};

@Component({
  selector: 'lib-sales-insights-activity-list',
  standalone: false,
  templateUrl: './sales-insights-activity-list.component.html',
  styleUrl: './sales-insights-activity-list.component.scss'
})
export class SalesInsightsActivityListComponent
  extends SalesInsightsBaseComponent<SalesInsightsActivitiesResponse>
  implements OnInit
{
  protected translate = inject(TranslateService);
  protected activityService = inject(SalesInsightsActivityService);
  private destroy = inject(DestroyRef);

  readonly skeletonRows = SKELETON_ROWS;

  sections: SalesInsightsActivityViewSection[] = [];
  columns: SalesInsightsActivityColumns = { customer: true, responsible: true };
  collapsed = new Set<SalesInsightsActivityGroup>();
  search = '';
  type = ALL;
  status = ALL;
  typeOptions: OptionsListMulti[] = [];
  statusOptions: OptionsListMulti[] = [];

  selected = new Set<string>();
  saving = new Set<string>();
  moveDay: string | null = null;
  moving = false;
  bulkBusy = false;
  bulkError: string | null = null;
  bulkResult: SalesInsightsBulkResult | null = null;

  private searchChanges = new Subject<string>();
  private filterChanges = new Subject<void>();

  ngOnInit(): void {
    this.refreshFilterOptions();
    this.collapsed = new Set((this.options.collapsedGroups as SalesInsightsActivityGroup[] | undefined) || []);
    super.ngOnInit();
  }

  get canBook(): boolean {
    return !!this.options.bookActivity;
  }

  get bulkActions(): boolean {
    return !!this.options.bulkActions;
  }

  get rowActions(): boolean {
    return this.options.rowActions !== false;
  }

  get filtered(): boolean {
    return !!this.search.trim() || !!this.type || !!this.status;
  }

  get showBulkBar(): boolean {
    return (this.bulkActions || this.rowActions) && (!!this.selected.size || !!this.bulkResult || !!this.bulkError);
  }

  get columnCount(): number {
    return (
      (this.bulkActions ? 1 : 0) +
      3 +
      (this.columns.customer ? 1 : 0) +
      (this.columns.responsible ? 1 : 0) +
      (this.rowActions ? 1 : 0)
    );
  }

  get visibleIds(): string[] {
    return this.sections
      .filter(section => !this.collapsed.has(section.group))
      .flatMap(section => section.rows.map(view => view.row.id));
  }

  get allSelected(): boolean {
    const ids = this.visibleIds;
    return ids.length > 0 && ids.every(id => this.selected.has(id));
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsActivitiesResponse> {
    return this.activityService.loadTypes().pipe(
      switchMap(() =>
        this.salesInsightsService.getData<SalesInsightsActivitiesResponse>(
          {
            endpoint: this.config.source?.endpoint || 'activities',
            params: {
              ...(this.config.source?.params || {}),
              search: this.search.trim(),
              type: this.type,
              status: this.status
            }
          },
          filters,
          this.config.accepts
        )
      )
    );
  }

  protected reloads(): Observable<unknown> {
    return merge(
      this.dashboardState.changes('activities', 'activityTypes'),
      this.searchChanges.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged()),
      this.filterChanges
    );
  }

  // The list draws its own empty state, which offers to clear the filters or book the first activity.
  protected isEmpty(_data: SalesInsightsActivitiesResponse | null): boolean {
    return false;
  }

  protected onData(data: SalesInsightsActivitiesResponse | null): void {
    this.refreshFilterOptions();
    const today = salesInsightsDayKey(new Date());
    const locale = salesInsightsLocale(this.translate);
    const groups = (this.options.groups as SalesInsightsActivityGroup[] | undefined)?.length
      ? (this.options.groups as SalesInsightsActivityGroup[])
      : undefined;
    this.sections = salesInsightsActivitySections(data?.rows || [], data?.groups, today, groups).map(section => ({
      group: section.group,
      labelKey: section.labelKey,
      icon: GROUP_ICONS[section.group],
      count: formatSalesInsightsAmount(section.count),
      shown: section.rows.length < section.count ? formatSalesInsightsAmount(section.rows.length) : null,
      rows: section.rows.map(row => this.toView(row, today, locale))
    }));
    const views = this.sections.flatMap(section => section.rows);
    this.columns = {
      customer: views.some(view => !!(view.row.customerName || view.row.customerId)),
      responsible: views.some(view => !!view.responsibleName)
    };
    const ids = new Set(this.visibleIds);
    this.selected = new Set([...this.selected].filter(id => ids.has(id)));
  }

  onSearch(value: string): void {
    this.search = value;
    this.searchChanges.next(value.trim());
  }

  selectFilter(filter: ActivityFilter, selected: string[]): void {
    const value = selected?.[0] || ALL;
    if (this[filter] === value) {
      return;
    }
    this[filter] = value;
    this.refreshFilterOptions();
    this.filterChanges.next();
  }

  clearFilters(): void {
    this.search = '';
    this.type = ALL;
    this.status = ALL;
    this.refreshFilterOptions();
    this.searchChanges.next('');
    this.filterChanges.next();
  }

  toggleGroup(group: SalesInsightsActivityGroup): void {
    const next = new Set(this.collapsed);
    if (next.has(group)) {
      next.delete(group);
    } else {
      next.add(group);
    }
    this.collapsed = next;
  }

  toggle(id: string, event: Event): void {
    event.stopPropagation();
    const next = new Set(this.selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selected = next;
    this.moving = false;
    this.bulkResult = null;
  }

  toggleAll(): void {
    this.selected = this.allSelected ? new Set() : new Set(this.visibleIds);
    this.bulkResult = null;
  }

  clearSelection(): void {
    this.selected = new Set();
    this.moving = false;
    this.moveDay = null;
    this.bulkError = null;
  }

  open(view: SalesInsightsActivityView): void {
    this.activityService
      .openActivity(view.row.id)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        complete: () => this.dashboardState.notifyChanged('activities'),
        error: () => (this.bulkError = 'SALES_INSIGHTS_LOAD_FAILED')
      });
  }

  openCustomer(view: SalesInsightsActivityView, event: Event): void {
    event.stopPropagation();
    if (view.row.customerId) {
      this.activityService.openCustomer(view.row.customerId);
    }
  }

  // Ticks the row at once and puts it back if the save fails; the reload then moves it to its new group.
  toggleDone(view: SalesInsightsActivityView, event: Event): void {
    event.stopPropagation();
    const id = view.row.id;
    if (this.saving.has(id)) {
      return;
    }
    const doneStatus = this.activityService.doneStatus(this.options.doneStatus);
    const status = view.row.done
      ? this.activityService.reopenStatus(this.options.reopenStatus, doneStatus)
      : doneStatus;
    if (!status) {
      this.bulkError = 'SALES_INSIGHTS_ACTIVITY_NO_DONE_STATUS';
      return;
    }
    const previous = { done: view.row.done, status: view.row.status, statusName: view.statusName };
    view.row.done = !view.row.done;
    view.row.status = status;
    view.statusName = this.activityService.statusName(status);
    this.bulkError = null;
    this.saving = new Set(this.saving).add(id);
    this.activityService
      .setStatus(id, status)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.saving = this.without(this.saving, id);
          this.dashboardState.notifyChanged('activities');
        },
        error: () => {
          this.saving = this.without(this.saving, id);
          view.row.done = previous.done;
          view.row.status = previous.status;
          view.statusName = previous.statusName;
          this.bulkError = 'SALES_INSIGHTS_ACTIVITY_SAVE_FAILED';
        }
      });
  }

  moveOne(view: SalesInsightsActivityView, event: Event): void {
    event.stopPropagation();
    this.selected = new Set([view.row.id]);
    this.bulkResult = null;
    this.bulkError = null;
    this.moveDay = null;
    this.moving = true;
  }

  markDone(): void {
    const doneStatus = this.activityService.doneStatus(this.options.doneStatus);
    if (!doneStatus) {
      this.bulkError = 'SALES_INSIGHTS_ACTIVITY_NO_DONE_STATUS';
      return;
    }
    this.runBulk(id => this.activityService.setStatus(id, doneStatus));
  }

  setMoveDay(date: Date | null): void {
    this.moveDay = date ? salesInsightsDayKey(date) : null;
  }

  moveSelected(): void {
    const day = this.moveDay;
    if (day) {
      this.runBulk(id => this.activityService.move(id, day));
    }
  }

  private runBulk(write: (id: string) => Observable<unknown>): void {
    const ids = [...this.selected];
    this.bulkBusy = true;
    this.bulkError = null;
    this.activityService
      .bulk(ids, write)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(result => {
        this.bulkBusy = false;
        this.bulkResult = result;
        this.moving = false;
        this.moveDay = null;
        this.selected = new Set();
        this.dashboardState.notifyChanged('activities');
      });
  }

  private toView(row: SalesInsightsActivityRow, today: string, locale: string): SalesInsightsActivityView {
    const date = salesInsightsParseDate(row.activityDate);
    const hasTime = !!date && (date.getHours() !== 0 || date.getMinutes() !== 0);
    const day = date ? salesInsightsDayKey(date) : '';
    const time = hasTime ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
    const responsibleName = row.customerResponsibleName || row.customerResponsible || '';
    return {
      row,
      day,
      time,
      due: this.dueView(date, day, today, time, row.done, locale),
      typeName: this.activityService.typeName(row.type),
      typeRgb: salesInsightsColor(this.activityService.typeColorIndex(row.type, this.options.typeColors)),
      typeIcon: this.activityService.typeIcon(row.type, this.options.typeIcons),
      statusName: this.activityService.statusName(row.status),
      responsibleName,
      responsibleInitials: salesInsightsInitials(responsibleName),
      responsibleRgb: salesInsightsColor(salesInsightsTypeColorIndex(row.customerResponsible || responsibleName, []))
    };
  }

  // Near days are named ("Idag", "3 dagar sen"), the rest show the date; the second line has what the first left out.
  private dueView(
    date: Date | null,
    day: string,
    today: string,
    time: string,
    done: boolean,
    locale: string
  ): SalesInsightsActivityDueView {
    const due = salesInsightsDue(day, today, done);
    if (!date || !due) {
      return { kind: 'later', label: '', sub: '' };
    }
    const sameYear = date.getFullYear() === new Date().getFullYear();
    const short = date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' })
    });
    const weekday = date.toLocaleDateString(locale, { weekday: 'short' });
    const t = (key: string, params?: object) => this.translate.instant(key, params);
    switch (due.kind) {
      case 'overdue':
        return {
          kind: due.kind,
          label:
            due.days === -1
              ? t('SALES_INSIGHTS_DUE_YESTERDAY')
              : t('SALES_INSIGHTS_DUE_DAYS_AGO', { count: -due.days }),
          sub: `${weekday} ${short}`
        };
      case 'today':
        return { kind: due.kind, label: t('SALES_INSIGHTS_DUE_TODAY'), sub: time };
      case 'tomorrow':
        return { kind: due.kind, label: t('SALES_INSIGHTS_DUE_TOMORROW'), sub: time };
      case 'soon':
        return { kind: due.kind, label: date.toLocaleDateString(locale, { weekday: 'long' }), sub: time || short };
      default:
        return { kind: due.kind, label: `${weekday} ${short}`, sub: time };
    }
  }

  private refreshFilterOptions(): void {
    this.typeOptions = this.toOptions(
      'SALES_INSIGHTS_ACTIVITY_ALL_TYPES',
      this.activityService.typeOptions(),
      this.type
    );
    this.statusOptions = this.toOptions(
      'SALES_INSIGHTS_ACTIVITY_ALL_STATUSES',
      this.activityService.statusOptions(),
      this.status
    );
  }

  private without(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    next.delete(id);
    return next;
  }

  private toOptions(allLabelKey: string, options: OptionsList[], selected: string): OptionsListMulti[] {
    return [
      { id: ALL, name: this.translate.instant(allLabelKey), selected: !selected },
      ...options.map(option => ({ id: option.id, name: option.name, selected: option.id === selected }))
    ];
  }
}
